<template>
    <section class="document-comments" :class="{ compact: props.compact, locked: props.locked }">
        <div class="comments-header">
            <h3>Comments</h3>
            <div class="comments-header-actions">
                <button
                    v-if="props.allowExpand && !props.locked"
                    class="comment-header-action-button"
                    type="button"
                    aria-label="Expand comments"
                    v-tooltip="'Expand comments'"
                    @click="openExpandedComments"
                >
                    <font-awesome-icon :icon="['fas', 'up-right-and-down-left-from-center']" />
                </button>
                <font-awesome-icon
                    v-if="props.locked"
                    class="comments-locked-icon"
                    :icon="['fas', 'lock-keyhole']"
                    aria-label="Comments locked"
                />
                <icon-with-count-badge
                    v-else
                    :icon="['fas', 'comment']"
                    :count="commentCount"
                    aria-label="Comment count"
                />
            </div>
        </div>
        <central-modal
            v-model:visible="expandedCommentsVisible"
            header="Comments"
            :size="{ width: '100rem', height: '85vh' }"
            @hide="handleExpandedCommentsHide"
        >
            <document-comments
                :key="expandedCommentsKey"
                class="expanded-comments-modal-content"
                :document-short-code="props.documentShortCode"
                :initial-comment-count="commentCount"
                :initial-comments="hasLoadedComments ? comments : null"
                :compact="false"
                :allow-expand="false"
                :auto-focus-composer="expandedCommentsAutoFocusComposer"
                :initial-composer-html="expandedCommentsInitialComposerHtml"
                :start-rich-text="expandedCommentsStartRichText"
                :initial-edit-comment-id="expandedCommentsInitialEditCommentId"
                :initial-reply-parent-id="expandedCommentsInitialReplyParentId"
                :initial-scroll-comment-id="expandedCommentsInitialScrollCommentId"
                :focus-target="expandedCommentsFocusTarget"
                @comment-count-updated="handleExpandedCommentCountUpdated"
            />
        </central-modal>
        <div v-if="props.locked" class="comments-locked-message">
            Comments are available when you have access to this report.
        </div>
        <div v-else-if="isLoading" class="comments-loader">
            <component-loader />
        </div>
        <template v-else>
            <div v-if="canComment" class="comment-composer-wrapper">
                <lean-rich-text-input
                    ref="newCommentInput"
                    placeholder="Add a comment"
                    input-label="Add a comment"
                    submit-label="Add comment"
                    :is-saving="isSavingComment"
                    :editor-style="props.compact ? 'height: 3rem' : 'height: 90px'"
                    :initial-html="props.initialComposerHtml"
                    :allow-rich-text="true"
                    :rich-text-toggle-mode="props.compact ? 'external' : 'inline'"
                    :initially-rich-text="props.startRichText && !props.compact"
                    :force-rich-text="shouldForceNewCommentRichText"
                    :auto-focus="shouldAutoFocusNewComment"
                    @request-rich-text="openExpandedComposer"
                    @submit="createComment"
                />
            </div>
            <div v-if="sortedComments.length" class="comments-list">
                <div
                    v-for="comment in visibleComments"
                    :key="comment.commentId"
                    class="comment-group"
                >
                    <document-comment-card
                        :comment="comment"
                        :compact="props.compact"
                        group-parent
                        :can-comment="canComment"
                        :logged-in="authState.loggedIn"
                        :is-editing="editingCommentId === comment.commentId"
                        :is-truncated="commentBodyIsTruncated(comment.commentId)"
                        :is-vote-pending="isCommentVotePending(comment.commentId)"
                        :deleting="deletingCommentId === comment.commentId"
                        @body-element="setCommentBodyElement"
                        @comment-element="setCommentElement"
                        @reply="replyToComment"
                        @edit="editComment"
                        @delete="confirmDeleteFromActions"
                        @vote="voteOnComment"
                        @open-full="openFullComment"
                    >
                        <template #editor>
                            <lean-rich-text-input
                                :key="`edit-${comment.commentId}`"
                                :ref="setActiveEditInput"
                                class="comment-editor"
                                placeholder="Edit comment"
                                input-label="Edit comment"
                                submit-label="Save comment"
                                cancel-label="Cancel edit"
                                show-cancel
                                :initial-html="comment.bodyHtml"
                                :initially-rich-text="shouldOpenEditAsRichText(comment)"
                                :force-rich-text="shouldForceEditRichText(comment.commentId)"
                                :auto-focus="shouldAutoFocusEdit(comment.commentId)"
                                :is-saving="isSavingComment"
                                :allow-rich-text="true"
                                :rich-text-toggle-mode="props.compact ? 'external' : 'inline'"
                                @request-rich-text="openExpandedEdit(comment.commentId)"
                                @submit="bodyHtml => updateComment(comment.commentId, bodyHtml)"
                                @cancel="cancelEdit"
                            />
                        </template>
                    </document-comment-card>
                    <document-comment-card
                        v-for="reply in getVisibleReplies(comment)"
                        :key="reply.commentId"
                        :comment="reply"
                        :compact="props.compact"
                        :parent-comment-id="comment.commentId"
                        is-reply
                        :can-comment="canComment"
                        :logged-in="authState.loggedIn"
                        :is-editing="editingCommentId === reply.commentId"
                        :is-truncated="commentBodyIsTruncated(reply.commentId)"
                        :is-vote-pending="isCommentVotePending(reply.commentId)"
                        :deleting="deletingCommentId === reply.commentId"
                        @body-element="setCommentBodyElement"
                        @comment-element="setCommentElement"
                        @reply="replyToComment"
                        @edit="editComment"
                        @delete="confirmDeleteFromActions"
                        @vote="voteOnComment"
                        @open-full="openFullComment"
                    >
                        <template #editor>
                            <lean-rich-text-input
                                :key="`edit-${reply.commentId}`"
                                :ref="setActiveEditInput"
                                class="comment-editor sub-comment-editor"
                                placeholder="Edit reply"
                                input-label="Edit reply"
                                submit-label="Save reply"
                                cancel-label="Cancel edit"
                                show-cancel
                                :initial-html="reply.bodyHtml"
                                :initially-rich-text="shouldOpenEditAsRichText(reply)"
                                :force-rich-text="shouldForceEditRichText(reply.commentId)"
                                :auto-focus="shouldAutoFocusEdit(reply.commentId)"
                                :is-saving="isSavingComment"
                                :allow-rich-text="true"
                                :rich-text-toggle-mode="props.compact ? 'external' : 'inline'"
                                @request-rich-text="openExpandedEdit(reply.commentId)"
                                @submit="bodyHtml => updateComment(reply.commentId, bodyHtml)"
                                @cancel="cancelEdit"
                            />
                        </template>
                    </document-comment-card>
                    <button
                        v-if="hasHiddenReplies(comment)"
                        class="comments-inline-more"
                        type="button"
                        @click="openFullComment(comment.commentId)"
                    >
                        View all replies
                        <font-awesome-icon :icon="['fas', 'arrow-right']" />
                    </button>
                    <transition name="reply-composer">
                        <div
                            v-if="canComment && activeReplyParentId === comment.commentId"
                            class="reply-composer"
                        >
                            <lean-rich-text-input
                                :ref="setActiveReplyInput"
                                placeholder="Add a reply"
                                input-label="Add a reply"
                                submit-label="Add reply"
                                cancel-label="Cancel reply"
                                show-cancel
                                :is-saving="isSavingComment"
                                :allow-rich-text="true"
                                :rich-text-toggle-mode="props.compact ? 'external' : 'inline'"
                                :initially-rich-text="props.startRichText && props.initialReplyParentId === comment.commentId"
                                :force-rich-text="shouldForceReplyRichText(comment.commentId)"
                                :auto-focus="shouldAutoFocusReply(comment.commentId)"
                                @request-rich-text="openExpandedReply(comment.commentId)"
                                @submit="bodyHtml => createComment(bodyHtml, comment.commentId)"
                                @cancel="cancelReply"
                            />
                        </div>
                    </transition>
                </div>
                <button
                    v-if="hasHiddenParentComments"
                    class="comments-view-all"
                    type="button"
                    @click="openExpandedComments"
                >
                    View full discussion
                    <font-awesome-icon :icon="['fas', 'arrow-right']" />
                </button>
            </div>
            <div v-else class="comments-empty">
                No comments yet.
            </div>
        </template>
    </section>
</template>

<script setup lang="ts">
import { authState } from '@lcp/http-auth';
import {
    computed, nextTick, onBeforeUnmount, onMounted, ref,
} from 'vue';
import IconWithCountBadge from '@/components/common/IconWithCountBadge.vue';
import LeanRichTextInput from '@/components/common/LeanRichTextInput.vue';
import CentralModal from '@/components/common/CentralModal.vue';
import ComponentLoader from '@/components/layout/ComponentLoader.vue';
import DocumentCommentCard from '@/components/deltaGpt/DocumentCommentCard.vue';
import { IDocumentComment } from '@/interfaces/deltaGpt/IDocumentComment.ts';
import { useUserAccessStore } from '@/stores/userAccessStore.ts';
import { useDocumentComments } from '@/composables/useDocumentComments.ts';

const props = withDefaults(defineProps<{
    documentShortCode: string;
    initialCommentCount?: number;
    initialComments?: Array<IDocumentComment> | null;
    compact?: boolean;
    locked?: boolean;
    allowExpand?: boolean;
    autoFocusComposer?: boolean;
    initialComposerHtml?: string;
    startRichText?: boolean;
    initialEditCommentId?: string | null;
    initialReplyParentId?: string | null;
    initialScrollCommentId?: string | null;
    focusTarget?: 'new-comment' | 'edit-comment' | 'reply' | null;
}>(), {
    initialCommentCount: 0,
    initialComments: null,
    compact: false,
    locked: false,
    allowExpand: true,
    autoFocusComposer: false,
    initialComposerHtml: '',
    startRichText: false,
    initialEditCommentId: null,
    initialReplyParentId: null,
    initialScrollCommentId: null,
    focusTarget: null,
});

defineOptions({
    name: 'DocumentComments',
});

const emit = defineEmits<{
    'comment-count-updated': [number];
}>();

const userAccessStore = useUserAccessStore();
const activeReplyParentId = ref<string | null>(null);
const editingCommentId = ref<string | null>(null);
const expandedCommentsVisible = ref<boolean>(false);
const expandedCommentsKey = ref<number>(0);
const expandedCommentsAutoFocusComposer = ref<boolean>(false);
const expandedCommentsInitialComposerHtml = ref<string>('');
const expandedCommentsStartRichText = ref<boolean>(false);
const expandedCommentsInitialEditCommentId = ref<string | null>(null);
const expandedCommentsInitialReplyParentId = ref<string | null>(null);
const expandedCommentsInitialScrollCommentId = ref<string | null>(null);
const expandedCommentsFocusTarget = ref<'new-comment' | 'edit-comment' | 'reply' | null>(null);
const commentBodyElements = new Map<string, HTMLElement>();
const commentBodyResizeObservers = new Map<string, ResizeObserver>();
const commentElements = new Map<string, HTMLElement>();
const truncatedCommentBodyIds = ref<Set<string>>(new Set());
const newCommentInput = ref<InstanceType<typeof LeanRichTextInput> | null>(null);
const activeReplyInput = ref<InstanceType<typeof LeanRichTextInput> | null>(null);
const activeEditInput = ref<InstanceType<typeof LeanRichTextInput> | null>(null);
const isSavingComment = ref<boolean>(false);

const PREVIEW_COMMENT_COUNT = 3;
const PREVIEW_REPLY_COUNT = 1;
const MAX_COMPACT_COMMENT_BODY_HEIGHT_REM = 10;

const sortedComments = computed<Array<IDocumentComment>>(() => [...comments.value]
    .sort(sortComments)
    .map(comment => ({
        ...comment,
        replies: [...comment.replies].sort(sortComments),
    })));

const shouldLimitCommentsPreview = computed<boolean>(() => props.allowExpand);

const visibleComments = computed<Array<IDocumentComment>>(() => (
    shouldLimitCommentsPreview.value
        ? sortedComments.value.slice(0, PREVIEW_COMMENT_COUNT)
        : sortedComments.value
));

const hasHiddenParentComments = computed<boolean>(() => (
    shouldLimitCommentsPreview.value && sortedComments.value.length > visibleComments.value.length
));

const shouldForceNewCommentRichText = computed<boolean>(() => (
    props.startRichText && props.focusTarget === 'new-comment' && !props.compact
));

const shouldAutoFocusNewComment = computed<boolean>(() => (
    props.focusTarget === 'new-comment' && !props.compact
));

const canComment = computed<boolean>(() => !props.locked && authState.value.loggedIn && userAccessStore.userAccess.isLcp);

const {
    comments,
    locallyInsertedCommentIds,
    isLoading,
    hasLoadedComments,
    deletingCommentId,
    commentCount,
    initialise,
    refreshComments,
    createComment,
    updateComment,
    voteOnComment,
    isCommentVotePending,
    confirmDeleteComment,
} = useDocumentComments({
    documentShortCode: props.documentShortCode,
    initialCommentCount: props.initialCommentCount,
    initialComments: props.initialComments,
    locked: computed(() => props.locked),
    canComment,
    isSavingComment,
    newCommentInput,
    activeReplyInput,
    activeEditInput,
    onCommentCountUpdated: count => emit('comment-count-updated', count),
    onReplyCreated: cancelReply,
    onCommentUpdated: cancelEdit,
});

function getDateTime (date?: string | null): number {
    return date ? new Date(date).getTime() : 0;
}

function sortComments (a: IDocumentComment, b: IDocumentComment): number {
    const localInsertDifference = Number(locallyInsertedCommentIds.value.has(b.commentId)) - Number(locallyInsertedCommentIds.value.has(a.commentId));
    if (localInsertDifference !== 0) return localInsertDifference;

    const scoreDifference = b.voteCount - a.voteCount;
    if (scoreDifference !== 0) return scoreDifference;

    return getDateTime(b.createdAt) - getDateTime(a.createdAt);
}

function getVisibleReplies (comment: IDocumentComment): Array<IDocumentComment> {
    return shouldLimitCommentsPreview.value
        ? comment.replies.slice(0, PREVIEW_REPLY_COUNT)
        : comment.replies;
}

function hasHiddenReplies (comment: IDocumentComment): boolean {
    return shouldLimitCommentsPreview.value && comment.replies.length > PREVIEW_REPLY_COUNT;
}

function isRichTextComment (html: string): boolean {
    return /<(h[1-6]|strong|b|em|i|u|ol|ul|li|a|img|blockquote|pre|code)\b/i.test(html);
}

async function startReply (parentCommentId: string): Promise<void> {
    activeReplyParentId.value = parentCommentId;
    cancelEdit();
    await nextTick();
    await waitForAnimationFrame();
    activeReplyInput.value?.scrollIntoView();
    await activeReplyInput.value?.focus(true);
    window.setTimeout(() => activeReplyInput.value?.scrollIntoView(), 250);
}

function cancelReply (): void {
    activeReplyParentId.value = null;
    activeReplyInput.value = null;
}

function setActiveReplyInput (input: unknown): void {
    activeReplyInput.value = input as InstanceType<typeof LeanRichTextInput> | null;
}

function setActiveEditInput (input: unknown): void {
    activeEditInput.value = input as InstanceType<typeof LeanRichTextInput> | null;
}

function setCommentBodyElement (commentId: string, element: unknown): void {
    const existingElement = commentBodyElements.get(commentId);

    if (existingElement === element) return;

    commentBodyResizeObservers.get(commentId)?.disconnect();
    commentBodyResizeObservers.delete(commentId);

    if (!(element instanceof HTMLElement)) {
        commentBodyElements.delete(commentId);
        setCommentBodyIsTruncated(commentId, false);
        return;
    }

    commentBodyElements.set(commentId, element);

    const observer = new ResizeObserver(() => updateCommentBodyDisplay(commentId));
    observer.observe(element);
    commentBodyResizeObservers.set(commentId, observer);

    nextTick(() => updateCommentBodyDisplay(commentId));
}

function commentBodyIsTruncated (commentId: string): boolean {
    return truncatedCommentBodyIds.value.has(commentId);
}

function updateCommentBodyDisplay (commentId: string): void {
    const element = commentBodyElements.get(commentId);
    if (!element) return;

    setCommentBodyIsTruncated(commentId, shouldTruncateCommentBody(element));
}

function shouldTruncateCommentBody (element: HTMLElement): boolean {
    return props.compact && element.scrollHeight > getMaxCompactCommentBodyHeightPx();
}

function getMaxCompactCommentBodyHeightPx (): number {
    const rootFontSize = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize);
    return MAX_COMPACT_COMMENT_BODY_HEIGHT_REM * rootFontSize;
}

function setCommentBodyIsTruncated (commentId: string, isTruncated: boolean): void {
    const nextTruncatedCommentBodyIds = new Set(truncatedCommentBodyIds.value);

    if (isTruncated) {
        nextTruncatedCommentBodyIds.add(commentId);
    } else {
        nextTruncatedCommentBodyIds.delete(commentId);
    }

    truncatedCommentBodyIds.value = nextTruncatedCommentBodyIds;
}

function setCommentElement (commentId: string, element: unknown): void {
    if (element instanceof HTMLElement) {
        commentElements.set(commentId, element);
        return;
    }

    commentElements.delete(commentId);
}

function waitForAnimationFrame (): Promise<void> {
    return new Promise(resolve => {
        window.requestAnimationFrame(() => resolve());
    });
}

function startEdit (comment: IDocumentComment): void {
    editingCommentId.value = comment.commentId;
    cancelReply();
}

function handleEditClick (comment: IDocumentComment): void {
    if (props.compact && isRichTextComment(comment.bodyHtml)) {
        openExpandedEdit(comment.commentId);
        return;
    }

    startEdit(comment);
}

function cancelEdit (): void {
    editingCommentId.value = null;
}

function replyToComment (_comment: IDocumentComment, parentCommentId: string): void {
    startReply(parentCommentId);
}

function editComment (comment: IDocumentComment): void {
    handleEditClick(comment);
}

function confirmDeleteFromActions (commentId: string): void {
    confirmDeleteComment(commentId);
}

function openExpandedComments (): void {
    openExpandedCommentModal({
        autoFocusComposer: false,
        startRichText: false,
    });
}

function openFullComment (commentId: string): void {
    openExpandedCommentModal({
        autoFocusComposer: false,
        startRichText: false,
        initialScrollCommentId: commentId,
    });
}

function openExpandedComposer (initialComposerHtml: string = ''): void {
    openExpandedCommentModal({
        autoFocusComposer: true,
        startRichText: true,
        initialComposerHtml,
    });
}

function openExpandedEdit (commentId: string): void {
    openExpandedCommentModal({
        autoFocusComposer: false,
        startRichText: true,
        initialEditCommentId: commentId,
        initialScrollCommentId: commentId,
        shouldFocusTarget: !props.compact,
    });
}

function openExpandedReply (parentCommentId: string): void {
    openExpandedCommentModal({
        autoFocusComposer: false,
        startRichText: true,
        initialReplyParentId: parentCommentId,
        initialScrollCommentId: parentCommentId,
    });
}

function openExpandedCommentModal (options: {
    autoFocusComposer: boolean;
    startRichText: boolean;
    initialComposerHtml?: string;
    initialEditCommentId?: string;
    initialReplyParentId?: string;
    initialScrollCommentId?: string;
    shouldFocusTarget?: boolean;
}): void {
    if (props.compact) {
        cancelEdit();
        cancelReply();
    }

    expandedCommentsAutoFocusComposer.value = options.autoFocusComposer;
    expandedCommentsInitialComposerHtml.value = options.initialComposerHtml ?? '';
    expandedCommentsStartRichText.value = options.startRichText;
    expandedCommentsInitialEditCommentId.value = options.initialEditCommentId ?? null;
    expandedCommentsInitialReplyParentId.value = options.initialReplyParentId ?? null;
    expandedCommentsInitialScrollCommentId.value = options.initialScrollCommentId ?? null;
    expandedCommentsFocusTarget.value = options.shouldFocusTarget === false ? null : getFocusTarget(options);
    expandedCommentsKey.value += 1;
    expandedCommentsVisible.value = true;
}

function getFocusTarget (options: {
    autoFocusComposer: boolean;
    initialEditCommentId?: string;
    initialReplyParentId?: string;
}): 'new-comment' | 'edit-comment' | 'reply' | null {
    if (options.initialEditCommentId) return 'edit-comment';
    if (options.initialReplyParentId) return 'reply';
    if (options.autoFocusComposer) return 'new-comment';
    return null;
}

async function handleExpandedCommentsHide (): Promise<void> {
    await refreshComments();
}

function handleExpandedCommentCountUpdated (count: number): void {
    emit('comment-count-updated', count);
}

const initialised = initialise();

onMounted(async () => {
    await initialised;
    await initialiseRequestedComposerState();
    await scrollToInitialComment();
});

onBeforeUnmount(() => {
    commentBodyResizeObservers.forEach(observer => observer.disconnect());
    commentBodyResizeObservers.clear();
    commentBodyElements.clear();
    commentElements.clear();
});

function shouldOpenEditAsRichText (comment: IDocumentComment): boolean {
    return isRichTextComment(comment.bodyHtml)
        || (props.startRichText && props.initialEditCommentId === comment.commentId);
}

function shouldForceEditRichText (commentId: string): boolean {
    return props.startRichText
        && props.focusTarget === 'edit-comment'
        && props.initialEditCommentId === commentId
        && !props.compact;
}

function shouldForceReplyRichText (parentCommentId: string): boolean {
    return props.startRichText
        && props.focusTarget === 'reply'
        && props.initialReplyParentId === parentCommentId
        && !props.compact;
}

function shouldAutoFocusEdit (commentId: string): boolean {
    return props.focusTarget === 'edit-comment'
        && props.initialEditCommentId === commentId
        && !props.compact;
}

function shouldAutoFocusReply (parentCommentId: string): boolean {
    return props.focusTarget === 'reply'
        && props.initialReplyParentId === parentCommentId
        && !props.compact;
}

async function initialiseRequestedComposerState (): Promise<void> {
    if (props.initialEditCommentId) {
        editingCommentId.value = props.initialEditCommentId;
        activeReplyParentId.value = null;
    }

    if (props.initialReplyParentId) {
        activeReplyParentId.value = props.initialReplyParentId;
        editingCommentId.value = null;
    }

    await nextTick();
    await focusTargetInput();
}

async function focusTargetInput (): Promise<void> {
    if (props.focusTarget === 'edit-comment') {
        await scrollToInitialComment();
        await focusInitialInput(activeEditInput);
        return;
    }

    if (props.focusTarget === 'reply') {
        await scrollToInitialComment();
        await focusInitialInput(activeReplyInput);
        return;
    }

    if (props.focusTarget === 'new-comment') {
        await focusInitialInput(newCommentInput);
    }
}

async function scrollToInitialComment (): Promise<void> {
    if (!props.initialScrollCommentId) return;

    await nextTick();
    await waitForAnimationFrame();

    const commentElement = commentElements.get(props.initialScrollCommentId);
    if (!commentElement) return;

    commentElement.scrollIntoView({
        block: 'center',
        behavior: 'smooth',
    });
}

async function focusInitialInput (inputRef: { value: InstanceType<typeof LeanRichTextInput> | null }): Promise<void> {
    await nextTick();
    await waitForModalFocusToSettle();

    await focusInput(inputRef);
}

async function focusInput (inputRef: { value: InstanceType<typeof LeanRichTextInput> | null }): Promise<void> {
    if (props.startRichText) {
        await inputRef.value?.openRichText();
        return;
    }

    await inputRef.value?.focus(true);
}

function waitForModalFocusToSettle (): Promise<void> {
    return new Promise(resolve => {
        window.setTimeout(resolve, 250);
    });
}
</script>

<style scoped lang="scss">
.document-comments {
    display: flex;
    flex-direction: column;
    gap: var(--defaultSpacing);

    &.locked {
        color: var(--neutral700);
    }

    &.compact {
        .comments-list {
            gap: var(--defaultSpacing);
        }

        .comment {
            padding: var(--smallSpacing);
        }

        .comment-body-wrapper.truncated {
            max-height: 10rem;
        }
    }
}

.comments-header,
.comment-meta {
    display: flex;
    align-items: center;
    gap: var(--smallSpacing);
    flex-wrap: wrap;
}

.comments-header {
    justify-content: space-between;

    h3 {
        margin: 0;
    }
}

.comments-header-actions {
    display: inline-flex;
    align-items: center;
    gap: var(--smallSpacing);
}

.comments-locked-icon {
    color: var(--lcpWatermelon);
}

.comment-header-action-button {
    display: inline-flex;
    align-items: center;
    width: 2rem;
    height: 2rem;
    padding: 0;
    justify-content: center;
    border: 0;
    background: transparent;
    color: var(--actionColour);
    cursor: pointer;

    &:hover,
    &:focus-visible {
        color: var(--accentColour);
        outline: none;
    }
}

.expanded-comments-modal-content {
    height: 100%;
    padding: var(--defaultSpacing);
    overflow-y: auto;
}

.comments-loader {
    display: flex;
    justify-content: center;
    min-height: 12rem;
}

.comments-locked-message {
    padding: var(--defaultSpacing);
    border: 1px dashed var(--neutral300);
    border-radius: var(--borderRadiusInner);
    background: color-mix(in srgb, var(--neutral100) 55%, transparent);
    color: var(--neutral700);
    font-size: var(--smallFontSize);
}

.comment-composer-wrapper {
    position: relative;
}

.comment-meta,
.comments-empty {
    color: var(--neutral700);
    font-size: var(--smallFontSize);
}

.comment-date-meta {
    display: inline-flex;
    align-items: center;
    gap: var(--smallSpacing);
}

.comment-meta-actions {
    display: grid;
    align-items: center;
    height: 1.75rem;
    justify-items: end;

    > * {
        grid-area: 1 / 1;
        transition: opacity 0.5s ease, transform 0.5s ease;
    }
}

.comment-edited {
    color: var(--neutral600);
    font-style: italic;
}

.comments-list {
    display: flex;
    flex-direction: column;
    gap: var(--largeSpacing);
}

.comment-group {
    border: 1px solid var(--neutral400);
    border-radius: var(--borderRadiusOuter);
    background: var(--neutral0);
}

.comments-inline-more,
.comments-view-all {
    display: inline-flex;
    align-items: center;
    gap: var(--smallSpacing);
    border: 0;
    background: transparent;
    color: var(--actionColour);
    cursor: pointer;
    font-size: var(--smallFontSize);
    font-weight: var(--fontWeightBold);

    &:hover,
    &:focus-visible {
        color: var(--accentColour);
        outline: none;
    }
}

.comments-inline-more {
    align-self: flex-start;
    margin: var(--smallSpacing);
    padding: 0 var(--defaultSpacing);
}

.comments-view-all {
    align-self: center;
    padding: var(--smallSpacing) var(--defaultSpacing);
}

.comment-editor {
    display: flex;
    flex-direction: column;
    gap: var(--smallSpacing);
    margin: 0 var(--defaultSpacing);
    margin: 0 var(--smallSpacing);
}

.reply-composer {
    margin: var(--smallSpacing) var(--defaultSpacing) var(--smallSpacing) var(--defaultSpacing);

    :deep(.lean-rich-text-input__textarea) {
        padding-top: 0.875rem;
        padding-bottom: 0.875rem;
        line-height: 1.25;
    }
}

.reply-composer-enter-active,
.reply-composer-leave-active {
    overflow: hidden;
    transition: max-height 0.22s ease, margin 0.22s ease, opacity 0.16s ease, transform 0.22s ease;
}

.reply-composer-enter-from,
.reply-composer-leave-to {
    max-height: 0;
    margin-top: 0;
    opacity: 0;
    transform: translateY(calc(-1 * var(--smallSpacing)));
}

.reply-composer-enter-to,
.reply-composer-leave-from {
    max-height: 14rem;
    margin-top: var(--defaultSpacing);
    opacity: 1;
    transform: translateY(0);
}

.comment-body-row {
    display: flex;
    align-items: end;
    gap: var(--defaultSpacing);
    padding: 0 var(--defaultSpacing);
    flex-wrap: wrap;
}

.comment-body-wrapper {
    flex: 0 0 max-content;
    max-width: 100%;
    min-width: 0;

    &.truncated {
        position: relative;
        overflow: hidden;
    }

    &.truncated::after {
        content: "";
        position: absolute;
        right: 0;
        bottom: 0;
        left: 0;
        height: 7rem;
        background: linear-gradient(to bottom, transparent, var(--neutral0) 70%);
        pointer-events: none;
    }
}

.comment.reply .comment-body-wrapper.truncated::after {
    background: linear-gradient(to bottom, transparent, var(--neutral100) 70%);
}

.comment-body {
    max-width: 100%;
    overflow-wrap: anywhere;
    word-break: normal;

    :deep(ul),
    :deep(ol) {
        margin-bottom: var(--defaultSpacing);
        padding-left: var(--extraLargeSpacing);
    }

    :deep(ul) {
        list-style: disc;
    }

    :deep(ol) {
        list-style: decimal;
    }

    :deep(li) {
        margin-bottom: var(--smallSpacing);
    }

    :deep(p),
    :deep(li),
    :deep(a),
    :deep(span) {
        overflow-wrap: anywhere;
        word-break: normal;
    }

    :deep(img) {
        display: block;
        max-width: 100%;
        height: auto;
        margin: var(--defaultSpacing) 0;
        border-radius: var(--borderRadiusInner);
    }
}

.comment-view-full {
    display: inline-flex;
    align-items: center;
    gap: var(--smallSpacing);
    flex: 0 0 auto;
    border: 0;
    background: transparent;
    color: var(--actionColour);
    cursor: pointer;
    font-size: var(--smallFontSize);
    font-weight: var(--fontWeightBold);

    &:hover,
    &:focus-visible {
        color: var(--accentColour);
        outline: none;
    }
}

.comment-controls-spacer {
    flex: 1 1 auto;
    min-width: var(--defaultSpacing);
}

.comment-controls-row {
    display: flex;
    align-items: center;
    gap: var(--smallSpacing);
    flex: 0 0 auto;
    margin-left: auto;
}

.comment-vote-control {
    flex: 0 0 auto;
}

.comment-action-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: var(--actionColour);
    cursor: pointer;

    &:hover,
    &:focus-visible {
        background: var(--neutral300);
        color: var(--accentColour);
        outline: none;
    }

    &:disabled {
        cursor: wait;
        opacity: 0.5;
    }
}

.comment {
    padding: var(--defaultSpacing);
    border: 1px solid var(--neutral400);
    border-radius: var(--borderRadiusOuter);
    background: var(--neutral0);

    &.reply {
        margin-top: var(--defaultSpacing);
        margin-inline: var(--defaultSpacing);
        border-color: var(--neutral300);
        background: var(--neutral100);
    }
}

.comment-meta {
    justify-content: space-between;
    margin-bottom: var(--defaultSpacing);
    padding-bottom: var(--smallSpacing);
    border-bottom: 1px solid var(--neutral300);
    margin-left: var(--defaultSpacing);
    margin-right: var(--defaultSpacing);
}

.comment-action-controls {
    display: flex;
    align-items: center;
    gap: var(--smallSpacing);
    opacity: 0;
    pointer-events: none;
    transform: translateY(-0.2rem);
    z-index: 1;
}

.comment.reply:hover > .comment-meta .comment-meta-actions.has-actions .comment-action-controls,
.comment.reply:focus-within > .comment-meta .comment-meta-actions.has-actions .comment-action-controls {
    opacity: 1;
    pointer-events: auto;
    transform: translateY(0);
}

.comment:hover:not(:has(> .comment.reply:hover)):not(:has(> .comment.reply:focus-within)) > .comment-meta .comment-meta-actions.has-actions .comment-action-controls,
.comment:focus-within:not(:has(> .comment.reply:hover)):not(:has(> .comment.reply:focus-within)) > .comment-meta .comment-meta-actions.has-actions .comment-action-controls {
    opacity: 1;
    pointer-events: auto;
    transform: translateY(0);
}

.comment:hover:not(:has(> .comment.reply:hover)):not(:has(> .comment.reply:focus-within)) > .comment-meta .comment-meta-actions.has-actions .comment-date-meta,
.comment:focus-within:not(:has(> .comment.reply:hover)):not(:has(> .comment.reply:focus-within)) > .comment-meta .comment-meta-actions.has-actions .comment-date-meta,
.comment.reply:hover > .comment-meta .comment-meta-actions.has-actions .comment-date-meta,
.comment.reply:focus-within > .comment-meta .comment-meta-actions.has-actions .comment-date-meta {
    opacity: 0;
    pointer-events: none;
    transform: translateY(0.2rem);
}

.comment:focus-visible {
    outline: 2px solid var(--actionColour);
    outline-offset: 2px;
}
</style>
